import { cn } from "@/lib/utils";
import type { LucideIcon } from "lucide-react";

type Tone = "primary" | "amber" | "emerald" | "blue" | "violet" | "cyan" | "rose" | "orange";

const TONE_MAP: Record<Tone, { from: string; to: string; glow: string; ring: string; icon: string }> = {
  primary: {
    from: "from-primary/35", to: "to-primary/5",
    glow: "shadow-[0_8px_24px_-6px_hsl(var(--primary)/0.55),inset_0_1px_0_0_hsl(var(--primary)/0.4)]",
    ring: "ring-1 ring-inset ring-primary/30", icon: "text-primary drop-shadow-[0_0_6px_hsl(var(--primary)/0.6)]",
  },
  amber: {
    from: "from-amber-400/40", to: "to-amber-500/5",
    glow: "shadow-[0_8px_24px_-6px_rgb(245_158_11_/_0.55),inset_0_1px_0_0_rgb(252_211_77_/_0.4)]",
    ring: "ring-1 ring-inset ring-amber-400/30", icon: "text-amber-300 drop-shadow-[0_0_6px_rgb(251_191_36_/_0.7)]",
  },
  emerald: {
    from: "from-emerald-400/40", to: "to-emerald-500/5",
    glow: "shadow-[0_8px_24px_-6px_rgb(16_185_129_/_0.55),inset_0_1px_0_0_rgb(110_231_183_/_0.4)]",
    ring: "ring-1 ring-inset ring-emerald-400/30", icon: "text-emerald-300 drop-shadow-[0_0_6px_rgb(52_211_153_/_0.7)]",
  },
  blue: {
    from: "from-sky-400/40", to: "to-sky-500/5",
    glow: "shadow-[0_8px_24px_-6px_rgb(56_189_248_/_0.55),inset_0_1px_0_0_rgb(125_211_252_/_0.4)]",
    ring: "ring-1 ring-inset ring-sky-400/30", icon: "text-sky-300 drop-shadow-[0_0_6px_rgb(56_189_248_/_0.7)]",
  },
  violet: {
    from: "from-violet-400/40", to: "to-violet-500/5",
    glow: "shadow-[0_8px_24px_-6px_rgb(139_92_246_/_0.55),inset_0_1px_0_0_rgb(196_181_253_/_0.4)]",
    ring: "ring-1 ring-inset ring-violet-400/30", icon: "text-violet-300 drop-shadow-[0_0_6px_rgb(167_139_250_/_0.7)]",
  },
  cyan: {
    from: "from-cyan-400/40", to: "to-cyan-500/5",
    glow: "shadow-[0_8px_24px_-6px_rgb(34_211_238_/_0.55),inset_0_1px_0_0_rgb(103_232_249_/_0.4)]",
    ring: "ring-1 ring-inset ring-cyan-400/30", icon: "text-cyan-300 drop-shadow-[0_0_6px_rgb(34_211_238_/_0.7)]",
  },
  rose: {
    from: "from-rose-400/40", to: "to-rose-500/5",
    glow: "shadow-[0_8px_24px_-6px_rgb(244_63_94_/_0.55),inset_0_1px_0_0_rgb(253_164_175_/_0.4)]",
    ring: "ring-1 ring-inset ring-rose-400/30", icon: "text-rose-300 drop-shadow-[0_0_6px_rgb(251_113_133_/_0.7)]",
  },
  orange: {
    from: "from-orange-400/40", to: "to-orange-500/5",
    glow: "shadow-[0_8px_24px_-6px_rgb(249_115_22_/_0.55),inset_0_1px_0_0_rgb(253_186_116_/_0.4)]",
    ring: "ring-1 ring-inset ring-orange-400/30", icon: "text-orange-300 drop-shadow-[0_0_6px_rgb(251_146_60_/_0.7)]",
  },
};

interface PremiumIconProps {
  icon: LucideIcon;
  tone?: Tone;
  size?: "sm" | "md" | "lg";
  className?: string;
}

/**
 * Premium glassy 3D icon tile with subtle inner highlight + tone-matched glow.
 * Looks luxe in dark mode, still tasteful in light mode.
 */
export function PremiumIcon({ icon: Icon, tone = "primary", size = "md", className }: PremiumIconProps) {
  const t = TONE_MAP[tone];
  const dim =
    size === "sm" ? "h-8 w-8 rounded-xl" : size === "lg" ? "h-12 w-12 rounded-2xl" : "h-10 w-10 rounded-2xl";
  const iconDim = size === "sm" ? "h-4 w-4" : size === "lg" ? "h-6 w-6" : "h-5 w-5";

  return (
    <span
      className={cn(
        "relative inline-flex items-center justify-center isolate",
        "bg-gradient-to-br backdrop-blur-md",
        dim, t.from, t.to, t.glow, t.ring,
        className
      )}
    >
      {/* glossy top highlight */}
      <span className="pointer-events-none absolute inset-x-1 top-0.5 h-1/2 rounded-t-[inherit] bg-gradient-to-b from-white/25 to-transparent opacity-70" />
      {/* radial glow behind icon */}
      <span className="pointer-events-none absolute inset-0 rounded-[inherit] bg-[radial-gradient(circle_at_50%_120%,hsl(var(--background))/0,transparent_60%)]" />
      <Icon className={cn("relative z-10", iconDim, t.icon)} strokeWidth={2.25} />
    </span>
  );
}
